<script setup>
import {useBaseStore} from "~/stores/base";
import ModalBackInStockNotification from "~/components/product/ModalBackInStockNotification.vue";

/** emits **/
const emits = defineEmits(["change"])

/** props **/
const props = defineProps({
  product: {
    default: {},
    required: true
  },
  currentColorIndex: {
    default: 0,
    required: true
  },
  currentSku: {
    default: "",
    required: true
  }
})

const isShowModalNotification = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const currentColor = computed(() => props.product.newVariantOptions?.colorList[props.currentColorIndex].color)
const currentSizeList = computed(() => props.product.optionConfig?.find(config => config.name === currentColor.value).sizeList)
const notificationEntranceVisible = computed(() => currentSizeList.value?.filter(item => item.inventory === 0).length > 0)

// 默认选中第一个sku
// immediate 监听 isMobile，保证在切换大小屏时也能默认选中第一个
// await nextTick() 保证在handleSelect存在后执行
// watch([currentSizeList, isMobile], async () => {
//   await nextTick()
//   if (currentSizeList.value) handleSelect({item: currentSizeList.value[0], index: 0})
// }, {immediate: true})

const handleSelect = (item) => {
  if (item.inventory === 0) return;
  emits("change", item.sku)
}
const handeSizeGuideClick = () => {
  useBaseStore().updateIsShowModalSizeCharts(true)
}
const handleNotificationClick = () => {
  isShowModalNotification.value = true
}
</script>

<template>
  <div class="module-product-size-mobile" v-if="isMobile">
    <div class="title">
      <span>Select a Size:</span>
      <div class="size-charts" @click="handeSizeGuideClick">Size Guide</div>
    </div>
    <div class="selections">
      <div
        v-for="(item, index) in currentSizeList"
        class="selection"
        :class="{
          active: item.sku === currentSku,
          disabled: item.inventory === 0,
          large: item.value.length >= 5
        }"
        @click="handleSelect(item)"
      >
        {{ item.value }}
        <img src="@/assets/img/icon-no-inventory.svg" alt="no-inventory" v-if="item.inventory === 0">
      </div>
    </div>
    <div class="notification-entrance" v-if="notificationEntranceVisible">
      <div class="btn-text" @click="handleNotificationClick">Back in stock notification</div>
    </div>
  </div>
  <div class="module-product-size-desktop" v-else>
    <div class="title">
      <span>Select a Size:</span>
      <div class="size-charts" @click="handeSizeGuideClick">Size Guide</div>
    </div>
    <div class="selections">
      <div
        v-for="(item, index) in currentSizeList"
        class="selection"
        :class="{
          active: item.sku === currentSku,
          disabled: item.inventory === 0,
          large: item.value.length >= 5
        }"
        @click="handleSelect(item)"
      >
        {{ item.value }}
        <img src="@/assets/img/icon-no-inventory.svg" alt="no-inventory" v-if="item.inventory === 0">
      </div>
    </div>
    <div class="notification-entrance" v-if="notificationEntranceVisible">
      <div class="btn-text" @click="handleNotificationClick">Back in stock notification</div>
    </div>
  </div>
  <modal-back-in-stock-notification :product="product" :open="isShowModalNotification" @close="isShowModalNotification = false"/>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-product-size-mobile {
  margin-top: 12px;
  padding: 0 16px;

  .title {
    /* Text sm/Regular */
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 20px; /* 142.857% */

    display: flex;
    justify-content: space-between;

    .size-charts {
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 650;
      line-height: 20px; /* 142.857% */
      text-decoration-line: underline;
      cursor: pointer;
    }
  }

  .selections {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .selection {
      flex-shrink: 0;
      min-width: 40px;
      height: 40px;
      border: 0.5px $color-gray-300 solid;
      display: flex;
      justify-content: center;
      align-items: center;

      /* Text xs/Regular */
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: 18px; /* 150% */

      cursor: pointer;

      &.large {
        padding: 0 8px;
      }
    }

    .disabled {
      background-color: $color-gray-100;
      color: $color-gray-400;
      border: none;
      position: relative;
      cursor: not-allowed;

      img {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%, 0);
      }

      //&:after {
      //  content: "Ⅹ";
      //  font-size: 30px;
      //  font-weight: lighter;
      //  position: absolute;
      //  width: 40px;
      //  height: 40px;
      //  display: flex;
      //  justify-content: center;
      //  align-items: center;
      //}
    }

    .active {
      border: 1.5px $color-gray-800 solid;
    }
  }

  .notification-entrance {
    margin-top: 5px;

    .btn-text {
      width: fit-content;
      padding-bottom: 0;
      font-family: "TWK Lausanne";
      font-size: 14.452px;
      font-style: normal;
      font-weight: 650;
      line-height: 20.645px; /* 142.857% */
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
      text-transform: none;
    }
  }
}

.module-product-size-desktop {
  margin-top: 24px;

  .title {
    max-width: 340px;
    /* Text sm/Regular */
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 20px; /* 142.857% */

    display: flex;
    justify-content: space-between;

    .size-charts {
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 650;
      line-height: 20px; /* 142.857% */
      text-decoration-line: underline;
      cursor: pointer;
    }
  }

  .selections {
    max-width: 340px;
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .selection {
      min-width: 40px;
      height: 40px;
      border: 0.5px $color-gray-300 solid;
      display: flex;
      justify-content: center;
      align-items: center;

      /* Text xs/Regular */
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: 18px; /* 150% */
      cursor: pointer;

      &.large {
        padding: 0 8px;
      }
    }

    .disabled {
      background-color: $color-gray-100;
      color: $color-gray-400;
      border: none;
      position: relative;
      cursor: not-allowed;

      img {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%, 0);
      }

      //&:after {
      //  content: "Ⅹ";
      //  font-size: 30px;
      //  font-weight: lighter;
      //  position: absolute;
      //  width: 40px;
      //  height: 40px;
      //  display: flex;
      //  justify-content: center;
      //  align-items: center;
      //}
    }

    .active {
      border: 1.5px $color-gray-800 solid;
    }
  }

  .notification-entrance {
    margin-top: 5px;

    .btn-text {
      width: fit-content;
      padding-bottom: 0;
      font-family: "TWK Lausanne";
      font-size: 14.452px;
      font-style: normal;
      font-weight: 650;
      line-height: 20.645px; /* 142.857% */
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
      text-transform: none;
    }
  }
}
</style>
